/**
 * Dual-catalog pricing helpers shared by PlansPanel, SubscriptionModal,
 * AutopayBar, and unused subscribe surfaces so they cannot sell the wrong catalog.
 */

export const V2_PERIODS = ["1_month", "3_months", "6_months", "12_months"];

export function planFamily(plan) {
  if (plan?.plan_family) return String(plan.plan_family);
  const n = String(plan?.plan_name || "").replace(/_v2$/, "");
  if (["basic", "advanced", "premium", "trial"].includes(n)) return n;
  return n || "basic";
}

export function catalogVersion(plan) {
  const v = Number(plan?.catalog_version);
  if (v === 2) return 2;
  if (String(plan?.plan_name || "").endsWith("_v2")) return 2;
  return 1;
}

export function isLegacyCatalog(plan) {
  return catalogVersion(plan) === 1;
}

export function isTrialPlan(plan) {
  return planFamily(plan) === "trial" || plan?.plan_name === "trial";
}

export function isV2Period(period) {
  return V2_PERIODS.includes(String(period || ""));
}

export function priceRow(plan, period) {
  const prices = Array.isArray(plan?.prices) ? plan.prices : [];
  return prices.find((row) => row.billing_period === period) || null;
}

export function planBase(plan, period) {
  if (!plan) return 0;
  if (catalogVersion(plan) === 2 || isV2Period(period)) {
    const row = priceRow(plan, period);
    if (row && row.amount != null) return parseFloat(row.amount) || 0;
    if (period === "1_month" || period === "trial") {
      return parseFloat(plan.monthly_amount || plan.amount) || 0;
    }
    return 0;
  }
  if (period === "yearly") return parseFloat(plan.yearly_amount) || 0;
  return parseFloat(plan.monthly_amount || plan.amount) || 0;
}

/** Legacy cards show a discounted monthly figure; v2 shows the period total. */
export function displayPrice(plan, period) {
  if (isTrialPlan(plan)) return planBase(plan, catalogVersion(plan) === 2 ? "1_month" : "trial");
  if (catalogVersion(plan) === 2 || isV2Period(period)) return planBase(plan, period);
  if (period === "yearly") {
    const yearly = parseFloat(plan.yearly_amount) || 0;
    const monthly = parseFloat(plan.monthly_amount || plan.amount) || 0;
    if (yearly > 0 && monthly > 0 && Math.abs(yearly - monthly * 11) < 1) return yearly;
    return monthly - parseFloat(plan.yearly_discount || 0);
  }
  return parseFloat(plan.monthly_amount || plan.amount) || 0;
}

export function paymentAmount(plan, period) {
  if (isTrialPlan(plan)) return planBase(plan, catalogVersion(plan) === 2 ? "1_month" : "trial");
  if (catalogVersion(plan) === 2 || isV2Period(period)) return planBase(plan, period);
  if (period === "yearly") {
    const yearly = parseFloat(plan.yearly_amount);
    if (yearly > 0) return yearly;
    return displayPrice(plan, "yearly") * 12;
  }
  return parseFloat(plan.monthly_amount || plan.amount) || 0;
}

/** Yearly toggle badge. One-month-free yearly prices say so; older plans keep their monthly saving. */
export function yearlyOfferLabel(plan) {
  if (!plan || isTrialPlan(plan)) return null;
  const monthly = parseFloat(plan.monthly_amount || plan.amount) || 0;
  const yearly = parseFloat(plan.yearly_amount) || 0;
  if (monthly > 0 && yearly > 0 && Math.abs(yearly - monthly * 11) < 1) return "1 month free";
  const discount = parseFloat(plan.yearly_discount) || 0;
  if (discount > 0) return `Save ₹${Math.round(discount).toLocaleString("en-IN")}/month`;
  return null;
}

export function savePercent(plan, period) {
  const p3 = planBase(plan, "3_months");
  const price = planBase(plan, period);
  const factor = period === "6_months" ? 2 : period === "12_months" ? 4 : 0;
  if (!p3 || !price || !factor) return 0;
  return Math.round((1 - price / (p3 * factor)) * 100);
}

export function periodLabel(period) {
  return (
    {
      monthly: "Monthly",
      yearly: "Yearly",
      trial: "Trial",
      "1_month": "1 Month",
      "3_months": "3 Months",
      "6_months": "6 Months",
      "12_months": "12 Months",
    }[period] || String(period || "")
  );
}

export function periodSuffix(period) {
  return (
    {
      monthly: "/mo",
      yearly: "/yr",
      trial: "/mo",
      "1_month": "/mo",
      "3_months": "/3 mo",
      "6_months": "/6 mo",
      "12_months": "/yr",
    }[period] || ""
  );
}

export function periodUnit(period) {
  return (
    {
      monthly: "/month",
      yearly: "/year",
      trial: "/month",
      "1_month": "/1 month",
      "3_months": "/3 months",
      "6_months": "/6 months",
      "12_months": "/12 months",
    }[period] || ""
  );
}

export function cyclePhrase(period) {
  if (period === "3_months") return "every 3 months";
  if (period === "6_months") return "every 6 months";
  if (period === "12_months" || period === "yearly") return "each year";
  if (period === "1_month" || period === "trial") return "once (no auto-pay)";
  return "each month";
}

export function checkoutPeriod(plan, billingPeriod) {
  if (isTrialPlan(plan)) return catalogVersion(plan) === 2 ? "1_month" : "trial";
  return billingPeriod;
}

export function isMostPopular(plan) {
  return planFamily(plan) === "advanced";
}

export function isCurrentPlan(subscription, plan) {
  if (!subscription || !plan) return false;
  if (subscription.plan_name && plan.plan_name) {
    return String(subscription.plan_name) === String(plan.plan_name);
  }
  return planFamily(subscription) === planFamily(plan)
    && catalogVersion(subscription) === catalogVersion(plan);
}

/** Add-ons stay monthly/yearly regardless of the v2 3/6/12 toggle. */
export function addonBillingPeriod(period) {
  if (period === "yearly" || period === "12_months") return "yearly";
  return "monthly";
}

export function grantPeriodsForPlan(plan) {
  if (!plan) return [{ value: "monthly", label: "1 month" }];
  if (isTrialPlan(plan) && catalogVersion(plan) === 2) {
    return [{ value: "1_month", label: "1 month" }];
  }
  if (catalogVersion(plan) === 2) {
    return [
      { value: "3_months", label: "3 months" },
      { value: "6_months", label: "6 months" },
      { value: "12_months", label: "12 months" },
    ];
  }
  if (isTrialPlan(plan)) return [{ value: "trial", label: "Trial" }];
  return [
    { value: "monthly", label: "1 month" },
    { value: "yearly", label: "1 year" },
  ];
}

export function parsePlansPayload(payload) {
  const all = Array.isArray(payload?.data) ? payload.data : [];
  const inferred = all.some((p) => catalogVersion(p) === 2) ? "v2" : "legacy";
  const catalog = payload?.catalog || inferred;
  const trial = all.find((p) => isTrialPlan(p)) || null;
  const paid = all.filter((p) => !isTrialPlan(p));
  return {
    catalog,
    periods: payload?.periods || (catalog === "v2"
      ? ["3_months", "6_months", "12_months"]
      : ["monthly", "yearly"]),
    hasUsedTrial: Boolean(payload?.has_used_trial),
    trial,
    paid,
    all,
  };
}
